import { getParsedQuery } from './queryString';
import _cloneDeep from 'lodash/cloneDeep';
import { SearchDateRangeFilterProps } from 'types/common/searchfilter';
import { NOT_INCLUDED_DATE_TERMS } from 'types/common/searchfilter';
import { SelectOption } from 'types/common/select';

/**
 * 범용 필터 유틸리티
 *
 * 어떤 페이지의 필터든 필터 정의만 있으면 자동으로 처리합니다.
 */

/**
 * 필터 값 타입 열거형
 *
 * 각 필터의 데이터 타입을 명확히 정의합니다.
 */
export enum FilterValueType {
  NUMBER_ARRAY = 'number[]',
  STRING_ARRAY = 'string[]',
  NUMBER = 'number',
  STRING = 'string',
  BOOLEAN = 'boolean',
}

/**
 * 필터 정의 인터페이스
 *
 * 각 필터의 설정을 정의하는 객체입니다.
 * 이 설정을 기반으로 자동으로 초기값, 파싱, 변환 등이 처리됩니다.
 */
export interface FilterDefinition {
  /** 필터의 고유 키 (camelCase 권장) */
  key: string;

  /** 필터 값의 타입 */
  type: FilterValueType;

  /** 필터의 기본값 */
  defaultValue: any;

  /**
   * API 호출 시 사용할 키 이름
   *
   * 예: keyword 필터는 API에서 'searchKeyword'로 전송되어야 하는 경우
   * ```typescript
   * keyword: {
   *   key: 'keyword',
   *   apiKey: 'searchKeyword', // API에서는 'searchKeyword'로 전송
   * }
   * ```
   */
  apiKey?: string;
}

export type FilterConfig = Record<string, FilterDefinition>;

/**
 * 배열 타입 필터 키 목록 생성
 */
export const generateArrayValueKeys = (filters: FilterConfig): string[] => {
  return Object.values(filters)
    .filter(filter => filter.type.endsWith('[]'))
    .map(filter => filter.key);
};

/**
 * 초기 검색 파라미터 생성
 */
export const generateInitialSearchParams = (filters: FilterConfig): Record<string, any> => {
  const params: Record<string, any> = {};
  Object.values(filters).forEach(filter => {
    params[filter.key] = filter.defaultValue;
  });
  return params;
};

/**
 * FilterForm 생성
 */
export const generateFilterForm = <T = Record<string, any>>(
  filters: FilterConfig,
  searchParams: Record<string, any>,
): T => {
  const form: Record<string, any> = {};
  Object.values(filters).forEach(filter => {
    form[filter.key] = searchParams[filter.key];
  });
  return form as T;
};

/**
 * URL 쿼리 스트링을 SearchParams로 변환
 *
 * @param filters 필터 설정
 * @param queryString URL 쿼리 스트링
 * @param customParser 커스텀 파싱 함수 (선택사항)
 */
export const parseSearchParams = <T = Record<string, any>>(
  filters: FilterConfig,
  queryString: string,
  customParser?: (key: string, parsedQuery: Record<string, string[]>, defaultValue: any) => any,
): T => {
  const parsedQuery = getParsedQuery(queryString);
  const result: Record<string, any> = {};

  Object.entries(filters).forEach(([key, config]) => {
    const queryValue = parsedQuery[key];
    const defaultValue = config.defaultValue;

    // 커스텀 파서가 있으면 우선 사용
    if (customParser) {
      const customValue = customParser(key, parsedQuery, defaultValue);
      if (customValue !== undefined) {
        result[key] = customValue;
        return;
      }
    }

    // 쿼리 값이 없으면 기본값 사용
    if (!queryValue) {
      result[key] = defaultValue;
      return;
    }

    // 타입에 따라 자동 파싱
    switch (config.type) {
      case FilterValueType.NUMBER_ARRAY:
        result[key] = queryValue.map((v: string) => Number(v));
        break;
      case FilterValueType.STRING_ARRAY:
        result[key] = queryValue;
        break;
      case FilterValueType.NUMBER:
        result[key] = Number(queryValue[0]);
        break;
      case FilterValueType.BOOLEAN:
        // [EN] Compare, don't `JSON.parse` — a hand-edited URL (`?mdPick=`, `?mdPick=1`)
        //      would throw on the empty string or leak the number `1` to the API.
        // [KO] `JSON.parse` 대신 비교한다. 손으로 고친 URL(`?mdPick=`, `?mdPick=1`)이면
        //      빈 문자열에서 예외가 나거나 숫자 `1` 이 그대로 API 로 새기 때문이다.
        result[key] = queryValue[0] === 'true';
        break;
      case FilterValueType.STRING:
      default:
        result[key] = queryValue[0];
        break;
    }
  });

  return result as T;
};

interface BaseSearchParams extends SearchDateRangeFilterProps {
  page: number;
  sort: number;
  limit: number;
}

/**
 * FilterConfig 기반으로 getFilterForm 함수를 생성
 */
export const createFilterFormGetter =
  <TFilterForm>(filterConfig: FilterConfig) =>
  (searchParams: Record<string, any>): TFilterForm => {
    return _cloneDeep(generateFilterForm<TFilterForm>(filterConfig, searchParams));
  };

/**
 * FilterConfig 기반으로 getSearchParams 파서를 생성
 */
export const createSearchParamsParser =
  <TSearchParams extends BaseSearchParams>(
    filterConfig: FilterConfig,
    initialParams: TSearchParams,
  ) =>
  (pageQueryString: string): TSearchParams => {
    const parsedQuery = getParsedQuery(pageQueryString);
    const { dateFrom, dateTo, dateTerm, page, sort, limit } = initialParams;
    const filterParams = parseSearchParams(filterConfig, pageQueryString);

    let dateTermQueryValue = dateTerm;
    if (parsedQuery['dateTerm']) {
      const parsedQueryValue = Number(parsedQuery['dateTerm'][0]);
      dateTermQueryValue = parsedQueryValue === NOT_INCLUDED_DATE_TERMS ? null : parsedQueryValue;
    }

    return {
      ...filterParams,
      dateFrom: parsedQuery['dateFrom']?.[0] ?? dateFrom,
      dateTo: parsedQuery['dateTo']?.[0] ?? dateTo,
      dateTerm: dateTermQueryValue,
      page: parsedQuery['page'] ? Number(parsedQuery['page'][0]) : page,
      sort: parsedQuery['sort'] ? Number(parsedQuery['sort'][0]) : sort,
      limit: parsedQuery['limit'] ? Number(parsedQuery['limit'][0]) : limit,
    } as TSearchParams;
  };

/**
 * Record<string, string> → SelectOption[] 변환 유틸리티
 */
export const toStringOptions = (labels: Record<string, string>): SelectOption<string>[] =>
  Object.entries(labels).map(([value, label]) => ({ label, value }));
